/* eslint-disable max-len */
export var METHOD;

(function (METHOD) {
  METHOD["METHOD_POST"] = "POST";
  METHOD["METHOD_GET"] = "GET";
  METHOD["METHOD_PUT"] = "PUT";
  METHOD["METHOD_DELETE"] = "DELETE";
})(METHOD || (METHOD = {}));
/**
 * Class that makes a simple HTTP request.
 *
 * Usage:
 * 1 - Extend this class
 *
 * 2 - Create the constructor calling super(METHOD, URL, DEFAULT_ERROR_MESSAGE)
 *   * METHOD: Constant String that can be METHOD_POST, METHOD_GET, METHOD_PUT, METHOD_DELETE
 *   * URL: String pointing to the WS address
 *   * DEFAULT_ERROR_MESSAGE: String that is going to be passed to the onFail() method whenever any unexpected error happens.
 *
 * 2.5 - Additional methods that can be used in the constructor:
 *   * this.addHeader(String, String): Pair String value that is going to be added to the request header.
 *   * this.setErrorMessage(Number, String): Error message that is going to be returned in the onFail() callback if the server response is the number defined in the pair.
 *
 * 3 - Override the method overrideRequest() returning a jsonObject
 *
 * 3.5 - Override optionally the method parseResponse() to map the response to something else
 *
 * 4 - Instantiate the created object with new Child() and define its callbacks:
 *   * .onPreExecution(() => void): Callback that is going to be executed before anything else.
 *   * .onSuccess(Object => void): Callback that is going to be executed whenever the server returns a 200 code. The object returned is a json representation of the server response.
 *   * .onFail(String => void): Callback that is going to be executed whenever the server returns a code that is not 200 or any other exception occurred. The String contains the message why it failed.
 *   * .onPostExecution(() => void): Callback that is going to be executed once the request is completed, whether it failed or succeeded.
 *
 * 5 - Call execute() to send the request.
 *
 * 5.5 - Call executeAsPromise() if you want the request to be returned as a promise.
 *
 * @abstract
 * @virtual
 */


var GenericRequest =
/** @class */
function () {
  /**
   * Main class constructor.
   *
   * @param {string} method WS request method. Can be METHOD.METHOD_POST, METHOD.METHOD_PUT, METHOD.METHOD_GET or METHOD.METHOD_DELETE.
   * @param {string} url Url that the WS is going to send the request.
   * @param {string} generalErrorMessage Message that is going to be passed as the argument for the fail callbacks when any unexpected error occurs.
   */
  function GenericRequest(method, url, generalErrorMessage) {
    if (method === void 0) {
      method = METHOD.METHOD_POST;
    } // --- Logging


    this.debug = console.debug;
    this.warn = console.warn;
    this.log = console.log; // --- Callbacks

    this.onPostExecutionFunctions = [];
    this.onPreExecutionFunctions = [];
    this.onPostResultFunctions = [];
    this.successFunctions = [];
    this.failFunctions = [];
    this.headers = {};
    this.generalErrorMessage = generalErrorMessage;
    this.errorMessages = {};
    this.method = method;
    this.url = url;
    this.callOnPostExecute = this.callOnPostExecute.bind(this);
    this.callOnPostResult = this.callOnPostResult.bind(this);
    this.callOnPreExecute = this.callOnPreExecute.bind(this);
    this.executeAsPromise = this.executeAsPromise.bind(this);
    this.sendRequest = this.sendRequest.bind(this);
    this.logRequest = this.logRequest.bind(this);
    this.logAndFail = this.logAndFail.bind(this);
    this.addHeader = this.addHeader.bind(this);
    this.success = this.success.bind(this);
    this.execute = this.execute.bind(this);
    this.error = this.error.bind(this);
  } ///////////////////////////////
  //
  // --- Public methods - Callback.
  //
  //
  //

  /**
   * Callbacks that are going to be executed before anything else.
   *
   * @param onPreExecutionFunctions The callbacks.
   * @returns {self} This instance allowing to chain callbacks.
   */


  GenericRequest.prototype.onPreExecution = function () {
    var onPreExecutionFunctions = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      onPreExecutionFunctions[_i] = arguments[_i];
    }

    this.onPreExecutionFunctions = onPreExecutionFunctions;
    return this;
  };
  /**
   * Callbacks that are going to be after the response has been parsed
   *
   * @param onPostResultFunctions The callbacks.
   * @returns {self} This instance allowing to chain callbacks.
   */


  GenericRequest.prototype.onPostResult = function () {
    var onPostResultFunctions = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      onPostResultFunctions[_i] = arguments[_i];
    }

    this.onPostResultFunctions = onPostResultFunctions;
    return this;
  };
  /**
   * Callbacks that are going to be executed once the request is completed before the response parsing.
   *
   * @param onPostExecutionFunctions The callback.
   * @returns {self} This instance allowing to chain callbacks.
   */


  GenericRequest.prototype.onPostExecution = function () {
    var onPostExecutionFunctions = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      onPostExecutionFunctions[_i] = arguments[_i];
    }

    this.onPostExecutionFunctions = onPostExecutionFunctions;
    return this;
  };
  /**
   * Callbacks that are going to be executed whenever the server returns a 2XX code. The object returned is a json representation of the server response.
   * The returned object can be mapped to another object using #parseResponse()
   *
   * @param successFunctions The callback
   * @returns {self} This instance allowing to chain callbacks.
   */


  GenericRequest.prototype.onSuccess = function () {
    var successFunctions = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      successFunctions[_i] = arguments[_i];
    }

    this.successFunctions = successFunctions;
    return this;
  };
  /**
   * Callbacks that are going to be executed whenever the server returns a code that is not 200 or any other exception occurred. The String contains the message explaining why it failed.
   *
   * @param failFunctions The callback
   * @returns {GenericRequest} This instance allowing to chain callbacks.
   */


  GenericRequest.prototype.onFail = function () {
    var failFunctions = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      failFunctions[_i] = arguments[_i];
    }

    this.failFunctions = failFunctions;
    return this;
  }; ///////////////////////////////
  //
  // --- Public methods - Class configuration.
  //
  //
  //

  /**
   * Add an additional header param to the already exiting ones.
   *
   * @param {string} key
   * @param {string} value
   */


  GenericRequest.prototype.addHeader = function (key, value) {
    this.headers[key] = value;
  };
  /**
   * Set an error message that is going to be passed to the fail functions when this WS fails with a specific httpCode.
   *
   * @param {number} code
   * @param {string} message
   */


  GenericRequest.prototype.setErrorMessage = function (code, message) {
    this.errorMessages[code] = message;
  };
  /**
   * Set an error message that is going to be passed to the timeOut callback when this WS gets timedOut.
   *
   * @param {string} message
   */


  GenericRequest.prototype.setTimeoutMessage = function (message) {
    this.timedOutMessage = message;
  };
  /**
   * Set an error message that is going to be passed to the fail functions when no connection is detected.
   *
   * @param {string} message
   */


  GenericRequest.prototype.setNoConnectionMessage = function (message) {
    this.noNetMessage = message;
  };
  /**
   * Whether to show or not the WS logs.
   *
   * @param {boolean} enable
   */


  GenericRequest.prototype.enableLogs = function (enable) {
    this.log = enable ? console.log : function () {
      return null;
    };
    this.warn = enable ? console.warn : function () {
      return null;
    };
    this.debug = enable ? console.debug : function () {
      return null;
    };
  };
  /**
   *
   * Method that should be overridden and return an object representing the request data.
   *
   * @return {RequestType}
   */


  GenericRequest.prototype.getRequest = function () {
    return undefined;
  };
  /**
   * Override to modify the response that is going to be passed to the onSuccess callbacks.
   *
   * @param responseObj
   * @return {ResponseType}
   */


  GenericRequest.prototype.parseResponse = function (responseObj) {
    return responseObj;
  };
  /**
   * Override to modify the string that is going to be passed to th e onFail callbacks.
   *
   * @param errorObj
   * @return {string}
   */


  GenericRequest.prototype.parseError = function (errorObj) {
    return errorObj;
  }; ///////////////////////////////
  //
  // --- Public methods - Execution.
  //
  //
  //

  /**
   * Finish and send the request.
   */


  GenericRequest.prototype.execute = function () {
    // noinspection JSIgnoredPromiseFromCall
    this.innerExecutionPromise();
  };
  /**
   * Overrideable execution promise.
   *
   * @return {Promise<void>}
   */


  GenericRequest.prototype.innerExecutionPromise = function () {
    return Promise.resolve(this.getRequest()).then(this.logRequest).then(this.callOnPreExecute).then(this.sendRequest).then(this.callOnPostExecute).then(this.success).catch(this.error).then(this.callOnPostResult);
  };
  /**
   * Execute this request as a promise instead.
   *
   * @return {Promise<ResponseType>}
   */


  GenericRequest.prototype.executeAsPromise = function () {
    var _this = this;

    return new Promise(function (resolve, reject) {
      _this.onSuccess(resolve);

      _this.onFail(reject);

      _this.execute();
    });
  }; ///////////////////////////////
  //
  // --- Inner methods - Execution.
  //
  //
  //

  /**
   * Inner request execution.
   *
   * @param request
   */


  GenericRequest.prototype.sendRequest = function (request) {
    return fetch(this.url, {
      method: this.method,
      headers: this.headers,
      body: this.method === METHOD.METHOD_GET ? null : request instanceof FormData ? request : JSON.stringify(request || "{}")
    }).catch(function (error) {
      return error;
    });
  };
  /**
   * Inner onPreExecute callbacks management.
   *
   * @param request
   */


  GenericRequest.prototype.callOnPreExecute = function (request) {
    return this.onPreExecutionFunctions.forEach(function (func) {
      return func();
    }) || request;
  };
  /**
   * Inner onPostExecution callbacks management.
   *
   * @param request
   */


  GenericRequest.prototype.callOnPostExecute = function (request) {
    return this.onPostExecutionFunctions.forEach(function (func) {
      return func();
    }) || request;
  };
  /**
   * Inner onPostExecution callbacks management.
   *
   * @param request
   */


  GenericRequest.prototype.callOnPostResult = function (request) {
    return this.onPostResultFunctions.forEach(function (func) {
      return func();
    }) || request;
  }; ///////////////////////////////
  //
  // --- Inner methods - Response parsers.
  //
  //
  //

  /**
   * Fail inner management.
   *
   * @param {any} error
   * @param {number} errorCode
   * @instance
   */


  GenericRequest.prototype.error = function (error, errorCode) {
    this.debug("RESPONSE: FAIL[" + this.constructor.name + "]");
    if (error instanceof Error) this.logAndFail("Exception " + error.message + "\n\n" + error.stack, error.message || this.generalErrorMessage);else if (this.errorMessages[errorCode]) this.logAndFail("Result code " + errorCode + "\n\n" + this.errorMessages[errorCode], this.errorMessages[errorCode] || this.generalErrorMessage);else if (error._bodyText) this.logAndFail("Error response\n\n" + JSON.stringify(error._bodyText, null, 2), error._bodyText);else if (error._timedOut) this.logAndFail("Timed out", this.timedOutMessage || this.generalErrorMessage);else if (errorCode === 0) this.logAndFail("No net", this.noNetMessage || this.generalErrorMessage);else this.logAndFail("Unknown error", this.generalErrorMessage);
  };
  /**
   * Success inner management.
   *
   * @param {status: number, _bodyText: string} response
   * @instance
   */


  GenericRequest.prototype.success = function (response) {
    var _this = this;

    if (!this.isResponseSuccess(response)) this.error(response, response.status);else {
      var responseObj_1;

      try {
        responseObj_1 = JSON.parse(response._bodyText || "{}");
      } catch (e) {
        responseObj_1 = response._bodyText;
      }

      this.debug("RESPONSE: SUCCESS[" + this.constructor.name + "]");
      this.debug("CODE " + response.status);
      this.debug("BODY");
      this.debug("\n" + JSON.stringify(responseObj_1, null, 2));
      this.successFunctions.forEach(function (successFunction) {
        return successFunction(_this.parseResponse(responseObj_1));
      });
    }
  };
  /**
   * Manage if response is success.
   *
   * @param {status: number, _bodyText: string} response
   * @instance
   */


  GenericRequest.prototype.isResponseSuccess = function (response) {
    return !(response instanceof Error || response.status < 200 || response.status > 299);
  }; ///////////////////////////////
  //
  // --- Inner methods - Logging.
  //
  //
  //

  /**
   * Logs the request
   * @param request
   * @instance
   */


  GenericRequest.prototype.logRequest = function (request) {
    this.debug("REQUEST: " + this.method + "[" + this.constructor.name + "]");
    this.debug("URL " + this.url);
    this.debug("HEADERS");
    this.debug("\n" + JSON.stringify(this.headers || "{}", null, 2));
    this.debug("BODY");
    this.debug("\n" + JSON.stringify(request || "{}", null, 2));
    return request;
  };
  /**
   * Inner logging for failed requests.
   *
   * @param warnMessage
   * @param failMessage
   */


  GenericRequest.prototype.logAndFail = function (warnMessage, failMessage) {
    var _this = this;

    if (warnMessage) this.warn(this.constructor.name + "\n" + warnMessage);
    if (failMessage) this.failFunctions.forEach(function (func) {
      return func(_this.parseError(failMessage));
    });
  };

  return GenericRequest;
}();

export { GenericRequest };